<template>
  <div class="min-h-screen" data-theme="red">
    <NavBar :signed-in="signedIn" :user-name="userName" active-page="news" />
    <div class="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
      <div class="flex items-center justify-between mb-8">
        <h1 class="text-2xl font-bold text-gray-900">{{ t('news.title') }}</h1>
        <a v-if="userAdmin" href="/news/new" class="bg-blue-700 text-white px-4 py-2 rounded-lg text-sm font-medium hover:bg-blue-800 transition">{{ t('news.create') }}</a>
      </div>

      <div v-if="flash" :class="['mb-4 px-4 py-3 rounded-lg text-sm', flash.type === 'success' ? 'bg-blue-100 text-blue-800' : 'bg-red-100 text-red-800']">{{ flash.message }}</div>

      <div v-if="items.length === 0" class="text-center py-12 text-gray-500">{{ t('news.no_news') }}</div>

      <div class="space-y-6">
        <div v-for="item in items" :key="item.id"
             @click="goToNews($event, item)"
             class="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden hover:shadow-md transition cursor-pointer">
          <img v-if="item.cover_image_url" :src="coverUrl(item)" class="w-full" />
          <div class="p-6">
            <span class="text-lg font-semibold text-gray-900">{{ item.title }}</span>
            <p class="text-xs text-gray-500 mt-1">{{ item.user.name }} · {{ formatDate(item.created_at) }}</p>
            <p class="text-sm text-gray-600 mt-2">{{ stripHtml(item.body).substring(0, 200) }}{{ stripHtml(item.body).length > 200 ? '...' : '' }}</p>
            <div class="flex items-center gap-3 mt-2">
              <span class="text-xs text-gray-400">{{ item.comments_count }} {{ t('news.comments') }}</span>
              <template v-if="userAdmin">
                <a :href="`/news/${item.id}/edit`" @click.stop class="text-xs text-blue-700 hover:underline">{{ t('news.edit') }}</a>
                <button @click.stop="confirmDelete(item)" class="text-xs text-red-500 hover:underline">{{ t('news.delete') }}</button>
              </template>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- Delete Modal -->
    <div v-if="showDeleteModal" class="fixed inset-0 bg-black/50 flex items-center justify-center z-50" @click.self="closeDeleteModal">
      <div class="bg-white rounded-xl shadow-xl w-full max-w-sm mx-4 p-6">
        <h2 class="text-lg font-bold text-gray-900 mb-2">{{ t('news.confirm_delete') }}</h2>
        <p class="text-sm text-gray-600 mb-6">{{ t('news.confirm_delete_message') }}</p>
        <div class="flex justify-end gap-3">
          <button @click="closeDeleteModal" class="px-4 py-2 text-sm font-medium text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200 transition">{{ t('news.cancel') }}</button>
          <button @click="deleteNews" :disabled="submitting" class="px-4 py-2 text-sm font-medium text-white bg-red-600 rounded-lg hover:bg-red-700 transition disabled:opacity-50">{{ submitting ? '...' : t('news.delete') }}</button>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup>
import { ref } from 'vue'
import { useI18n } from 'vue-i18n'
import NavBar from './NavBar.vue'
import { croppedCoverUrl } from './coverCrop.js'

const props = defineProps({ signedIn: Boolean, userName: String, userId: Number, userAdmin: Boolean, news: Array, csrfToken: String })
const { t, locale } = useI18n()
const items = ref([...props.news])
const showDeleteModal = ref(false)
const submitting = ref(false)
const deleteTarget = ref(null)
const flash = ref(null)

function goToNews(e, item) { window.location.href = `/news/${item.id}` }

function coverUrl(item) {
  return croppedCoverUrl(item.cover_image_url, item.cover_position)
}

function formatDate(d) { return new Date(d).toLocaleDateString(undefined, { year: 'numeric', month: 'long', day: 'numeric' }) }
function stripHtml(html) { const tmp = document.createElement('div'); tmp.innerHTML = html; return tmp.textContent || tmp.innerText || '' }
function showFlash(msg, type = 'success') { flash.value = { message: msg, type }; setTimeout(() => { flash.value = null }, 4000) }

function confirmDelete(item) { deleteTarget.value = item; showDeleteModal.value = true }
function closeDeleteModal() { showDeleteModal.value = false; deleteTarget.value = null }

async function deleteNews() {
  submitting.value = true
  try {
    const res = await fetch(`/news/${deleteTarget.value.id}`, {
      method: 'DELETE',
      headers: { 'X-CSRF-Token': props.csrfToken, 'Accept': 'application/json' }
    })
    if (res.ok) { items.value = items.value.filter(n => n.id !== deleteTarget.value.id); showFlash(t('news.deleted')); closeDeleteModal() }
  } catch { showFlash(t('news.error'), 'error') }
  finally { submitting.value = false }
}
</script>
